import { useCallback, useMemo, useState } from "react";

import { Tooltip } from "antd";
import { clsx } from "clsx";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { RVIcon, RVIconSizeOptions, RVButton, numberPrettyFormatter } from "@regal-voice/ui";
import { ActiveUsersLink } from "Pages/orchestration/Pages/journeys/ActiveUsers/Components/ActiveUsersLink";
import { JOURNEY_USER_COUNT_CUTOFF_DATE } from "Pages/orchestration/Pages/journeys/constants";
import { useFlags } from "Services/FeatureFlagService";
import { selectJourneyId, selectJourneyMetaData, selectJourneyStats } from "Services/state/journeys/JourneyStateSlice";

import styles from "./JourneyLiveStats.module.scss";

type JourneyLiveStatsProps = {
    onViewExecutions?: () => void;
};

export function JourneyLiveStats({ onViewExecutions }: JourneyLiveStatsProps) {
    const journeyStats = useSelector(selectJourneyStats);
    const journeyId = useSelector(selectJourneyId);
    const { originalCreatedAt } = useSelector(selectJourneyMetaData);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const { journeyExecutionMonitor } = useFlags();
    const isJourneyCreatedAfterCutoffDate = dayjs(originalCreatedAt).isAfter(dayjs(JOURNEY_USER_COUNT_CUTOFF_DATE));

    const isNewJourney = useMemo(() => !journeyId || ["new", "duplicate"].includes(journeyId), [journeyId]);

    const handleViewExecutions = useCallback(() => {
        setTooltipOpen(false);
        onViewExecutions?.();
    }, [onViewExecutions]);

    const getTitle = useMemo(() => {
        if (!isJourneyCreatedAfterCutoffDate) {
            return;
        }
        return (
            <div key="statistics-dynamic" className={styles.countersContainer}>
                <div className="flex-row justify-between">
                    <ActiveUsersLink journeyId={journeyId} text="Contacts in Progress " />
                    <ActiveUsersLink journeyId={journeyId}>
                        <b>{numberPrettyFormatter(journeyStats?.inProgress)}</b>
                    </ActiveUsersLink>
                </div>
                <div className="flex-row justify-between">
                    Total Finished <b>{numberPrettyFormatter(journeyStats?.finished)}</b>
                </div>
                <div className="flex-row justify-between">
                    Total Stopped <b>{numberPrettyFormatter(journeyStats?.stopped)}</b>
                </div>
                {journeyExecutionMonitor && onViewExecutions && (
                    <div className="flex-row justify-between">
                        <RVButton type="link" className={styles.viewExecutionsLink} onClick={handleViewExecutions}>
                            Search Executions
                        </RVButton>
                    </div>
                )}
                <div className={clsx("note", styles.liveStatsNote)}>Data updated every 15 seconds</div>
            </div>
        );
    }, [
        isJourneyCreatedAfterCutoffDate,
        journeyId,
        journeyStats?.inProgress,
        journeyStats?.finished,
        journeyStats?.stopped,
        journeyExecutionMonitor,
        onViewExecutions,
        handleViewExecutions,
    ]);

    if (!isJourneyCreatedAfterCutoffDate || isNewJourney) {
        return <></>;
    }

    return (
        <Tooltip
            title={getTitle}
            trigger="click"
            placement="bottomRight"
            color="white"
            open={tooltipOpen}
            onOpenChange={setTooltipOpen}
        >
            <RVButton
                icon={
                    <RVIcon
                        icon="arrow-down"
                        size={RVIconSizeOptions.XSMALL}
                        className={tooltipOpen ? styles.expandToggle : ""}
                    />
                }
                className={styles.journeyStats}
            >
                <div style={{ marginRight: "10px" }}>Journey Stats</div>
            </RVButton>
        </Tooltip>
    );
}
